import type { RouteRecordRaw } from 'vue-router'
import type { ModuleNavKey } from '@/shared/layout/module-nav-types'
import type { FactoryImportListPurpose } from '@/features/onsite/api/factory-import.api'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { zaloRoutingLegacyRoute, zaloRoutingRoute } from '@/features/zalo-routing/routes/zalo-routing.route'

function factoryImportPage(
  path: string,
  name: string,
  lockedPurpose: FactoryImportListPurpose,
  pageTitleKey: string,
  pageSubtitleKey: string,
): RouteRecordRaw {
  return {
    path,
    name,
    component: () => import('@/features/onsite/pages/OnsiteFactoryImportPage.vue'),
    props: {
      moduleNav: 'factoryImport',
      lockedPurpose,
      pageTitleKey,
      pageSubtitleKey,
    },
    meta: {
      permissions: [
        'onsite.view',
        'onsite.edit',
        'factory_list.import',
        'attendance.import',
        'factory_data.confirm',
        'employment.match',
      ],
      pageTitleKey,
      pageSubtitleKey,
    },
  }
}

function sectionPage(
  path: string,
  name: string,
  moduleNav: ModuleNavKey,
  pageTitleKey: string,
  meta?: Record<string, unknown>,
): RouteRecordRaw {
  return {
    path,
    name,
    component: () => import('@/shared/pages/ModuleSectionPage.vue'),
    meta: { moduleNav, pageTitleKey, ...meta },
  }
}

export const authenticatedChildren: RouteRecordRaw[] = [
  // 01 Data center
  {
    path: 'data-center',
    name: 'data-center',
    component: () => import('@/features/dashboard/pages/DashboardPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'nav.items.dataOverview', moduleNav: 'dataCenter' },
  },
  {
    path: 'data-center/executive',
    name: 'data-center-executive',
    component: () => import('@/features/analytics/pages/ExecutiveCockpitPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { fullBleed: true, permission: 'dashboard.view', pageTitleKey: 'analyticsV3.nav.executive', moduleNav: 'dataCenter' },
  },
  {
    path: 'data-center/operations',
    name: 'data-center-operations-legacy',
    component: () => import('@/features/analytics/pages/AnalyticsExecutivePage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV3.nav.executive', moduleNav: 'dataCenter' },
  },
  {
    path: 'data-center/team-clients',
    name: 'data-center-team-matrix',
    component: () => import('@/features/analytics/pages/AnalyticsTeamClientPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV3.nav.teamClients' },
  },
  {
    path: 'data-center/team-clients/teams',
    name: 'data-center-team-ranking',
    component: () => import('@/features/analytics/pages/AnalyticsTeamRankingPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV4.teamClientEntity.teamsTitle' },
  },
  {
    path: 'data-center/team-clients/enterprises',
    name: 'data-center-team-enterprises',
    component: () => import('@/features/analytics/pages/AnalyticsTeamEnterprisesPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV4.teamClientEntity.enterprisesTitle' },
  },
  {
    path: 'data-center/relationships/teams',
    redirect: to => ({ name: 'data-center-team-matrix', query: to.query }),
  },
  {
    path: 'data-center/relationships',
    name: 'data-center-matrix',
    component: () => import('@/features/analytics/pages/AnalyticsOverviewPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV3.nav.relationships' },
  },
  {
    path: 'data-center/relationships/suppliers',
    name: 'data-center-suppliers',
    component: () => import('@/features/analytics/pages/AnalyticsSuppliersPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV1.suppliers.title' },
  },
  {
    path: 'data-center/relationships/enterprises',
    name: 'data-center-enterprises',
    component: () => import('@/features/analytics/pages/AnalyticsEnterprisesPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV1.enterprises.title' },
  },
  {
    path: 'data-center/workers',
    name: 'data-center-workers',
    component: () => import('@/features/analytics/pages/AnalyticsWorkersPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV1.workers.title' },
  },
  {
    path: 'data-center/sources',
    name: 'data-center-sources',
    component: () => import('@/features/analytics/pages/AnalyticsSourcesPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV2.sources.title' },
  },
  {
    path: 'data-center/conversion',
    name: 'data-center-conversion',
    component: () => import('@/features/analytics/pages/AnalyticsConversionPage.vue'),
    props: { moduleNav: 'dataCenter' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'analyticsV2.conversion.title' },
  },
  { path: 'data-center/realtime', redirect: to => ({ name: 'data-center', query: to.query }) },
  { path: 'data-center/forecast', redirect: to => ({ name: 'data-center', query: to.query }) },
  { path: 'data-center/suppliers', redirect: to => ({ name: 'data-center-suppliers', query: to.query }) },
  { path: 'data-center/enterprises', redirect: to => ({ name: 'data-center-enterprises', query: to.query }) },
  ...(import.meta.env.DEV ? [{
    path: 'dev/analytics-ui-lab',
    name: 'analytics-ui-lab',
    component: () => import('@/features/analytics/pages/AnalyticsUiLabPage.vue'),
    meta: { permission: 'dashboard.view', pageTitleKey: 'Analytics UI Lab' },
  } satisfies RouteRecordRaw] : []),

  // 10 Reports
  { path: 'reports', redirect: to => ({ name: 'recruitment-reports', query: to.query }) },
  { path: 'reports/funnel', redirect: to => ({ name: 'recruitment-reports', query: to.query }) },
  { path: 'reports/conversion', redirect: to => ({ name: 'recruitment-reports', query: to.query }) },
  { path: 'reports/team-kpi', name: 'reports-team-kpi', redirect: to => ({ name: 'recruitment-reports', query: to.query }) },
  {
    path: 'reports/enterprise-delivery',
    name: 'reports-enterprise-delivery',
    component: () => import('@/features/reports/pages/ReportsEnterpriseDeliveryPage.vue'),
    props: { moduleNav: 'reports' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'nav.items.reportsEnterpriseDelivery' },
  },
  {
    path: 'reports/traffic-sources',
    name: 'reports-traffic-sources',
    component: () => import('@/features/reports/pages/ReportsTrafficSourcesPage.vue'),
    props: { moduleNav: 'reports' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'nav.items.reportsTrafficSources' },
  },
  {
    path: 'reports/media-funnel',
    name: 'reports-media-funnel',
    component: () => import('@/features/reports/pages/ReportsMediaFunnelPage.vue'),
    props: { moduleNav: 'reports' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'nav.items.reportsMediaFunnel' },
  },
  {
    path: 'reports/channel-suppliers',
    name: 'reports-channel-suppliers',
    component: () => import('@/features/reports/pages/ReportsChannelSuppliersPage.vue'),
    props: { moduleNav: 'reports' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'nav.items.reportsChannelSuppliers' },
  },
  {
    path: 'reports/employment',
    name: 'reports-employment',
    component: () => import('@/features/reports/pages/ReportsEmploymentPage.vue'),
    props: { moduleNav: 'reports' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'nav.items.reportsEmployment' },
  },
  {
    path: 'reports/finance-forecast',
    name: 'reports-finance-forecast',
    component: () => import('@/features/reports/pages/ReportsFinanceForecastPage.vue'),
    props: { moduleNav: 'reports' },
    meta: { permission: 'dashboard.view', pageTitleKey: 'nav.items.reportsFinanceForecast' },
  },

  // 02 Recruitment center
  { path: 'recruitment/inbox', name: 'inbox', redirect: { name: 'recruiter-workbench' } },
  {
    path: 'recruitment/workbench',
    name: 'recruiter-workbench',
    component: () => import('@/features/recruitment/pages/RecruiterWorkbenchPage.vue'),
    props: { moduleNav: 'recruitment' },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.recruiterWorkbench',
    },
  },
  {
    path: 'recruitment/reports',
    name: 'recruitment-reports',
    component: () => import('@/features/team-lead/pages/TeamLeadPage.vue'),
    meta: {
      permissions: ['dashboard.view', 'team.kpi.view', 'assignments.manage', 'users.manage'],
      pageTitleKey: 'nav.items.recruitmentReports',
    },
  },
  {
    path: 'recruitment/team-members',
    name: 'team-members',
    component: () => import('@/features/team-lead/pages/TeamMembersPage.vue'),
    meta: {
      permissions: ['team.members.view', 'team.members.manage'],
      pageTitleKey: 'nav.items.teamMembers',
    },
  },
  {
    path: 'recruitment/my-commission',
    name: 'recruiter-my-commission',
    component: () => import('@/features/recruitment/pages/MyCommissionPage.vue'),
    props: { moduleNav: 'recruitment' },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'commission.title',
    },
  },
  {
    path: 'recruitment/care-alerts',
    name: 'recruitment-care-alerts',
    component: () => import('@/features/recruitment/pages/CareAlertsPage.vue'),
    meta: {
      permission: 'recruitment_center.view',
      moduleNav: 'recruitment',
      pageTitleKey: 'nav.items.careAlerts',
    },
  },
  {
    path: 'recruitment/my-resources',
    name: 'recruitment-my-resources',
    component: () => import('@/features/leads/pages/LeadLibraryPage.vue'),
    props: {
      fixedWorkbenchTab: 'my_sources',
      lockWorkbenchTabs: true,
      moduleNav: 'recruitment',
      pageTitleKey: 'nav.items.myResources',
    },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.myResources',
      // Same LeadLibraryPage.vue instance across these 3 tab routes — a stable pageKey
      // stops AppShell's router-view :key from remounting the whole page on every tab
      // click (which was re-running loadMeta()/onMounted from scratch instead of the
      // existing route watcher's lightweight load()-only refresh).
      pageKey: 'recruitment-my-resources-workbench',
    },
  },
  // My resources — sub-views: pending follow-up, actively following (same page, different workbench_tab)
  {
    path: 'recruitment/pending-follow-ups',
    name: 'recruitment-pending-follow-ups',
    component: () => import('@/features/leads/pages/LeadLibraryPage.vue'),
    props: {
      fixedWorkbenchTab: 'pending_fu',
      lockWorkbenchTabs: true,
      moduleNav: 'recruitment',
      pageTitleKey: 'leadLibrary.workbench.tabs.pending_fu',
    },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'leadLibrary.workbench.tabs.pending_fu',
      pageKey: 'recruitment-my-resources-workbench',
    },
  },
  {
    path: 'recruitment/follow-ups',
    name: 'recruitment-follow-ups',
    component: () => import('@/features/leads/pages/LeadLibraryPage.vue'),
    props: {
      fixedWorkbenchTab: 'following',
      lockWorkbenchTabs: true,
      moduleNav: 'recruitment',
      pageTitleKey: 'nav.items.activelyFollowing',
    },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.activelyFollowing',
      pageKey: 'recruitment-my-resources-workbench',
    },
  },
  {
    path: 'recruitment/registered',
    name: 'recruitment-registered',
    component: () => import('@/features/leads/pages/LeadLibraryPage.vue'),
    props: {
      fixedWorkbenchTab: 'registered',
      lockWorkbenchTabs: true,
      moduleNav: 'recruitment',
      pageTitleKey: 'leadLibrary.workbench.tabs.registered',
    },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'leadLibrary.workbench.tabs.registered',
      pageKey: 'recruitment-my-resources-workbench',
    },
  },
  {
    path: 'recruitment/returned-public',
    name: 'recruitment-returned-public',
    component: () => import('@/features/leads/pages/LeadLibraryPage.vue'),
    props: {
      fixedWorkbenchTab: 'returned_public',
      lockWorkbenchTabs: true,
      moduleNav: 'recruitment',
      pageTitleKey: 'leadLibrary.workbench.tabs.returned_public',
    },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'leadLibrary.workbench.tabs.returned_public',
      pageKey: 'recruitment-my-resources-workbench',
    },
  },
  {
    path: 'recruitment/zalo-follow-ups',
    name: 'recruitment-zalo-follow-ups',
    component: () => import('@/features/recruitment/pages/ZaloFollowUpPage.vue'),
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.zaloFollowUps',
      moduleNav: 'recruitment',
    },
  },
  {
    path: 'recruitment/zalo-accounts',
    name: 'recruitment-zalo-accounts',
    component: () => import('@/features/recruitment/pages/ZaloAccountsPage.vue'),
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.zaloAccounts',
      moduleNav: 'recruitment',
    },
  },
  {
    path: 'recruitment/follow-up-history',
    name: 'recruitment-follow-up-history',
    component: () => import('@/features/recruitment/pages/FollowUpHistoryPage.vue'),
    meta: {
      permissions: ['recruitment_center.view', 'lead.my_view', 'lead.team_view'],
      pageTitleKey: 'nav.items.followUpRecords',
      moduleNav: 'recruitment',
    },
  },
  {
    path: 'recruitment/my-applications',
    name: 'recruitment-my-applications',
    component: () => import('@/features/applications/pages/ApplicationListPage.vue'),
    props: {
      scopeMine: true,
      moduleNav: 'recruitment',
      pageTitleKey: 'nav.items.myApplications',
    },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.myApplications',
    },
  },
  {
    path: 'recruitment/my-applications/:id',
    name: 'recruitment-application-detail',
    component: () => import('@/features/applications/pages/ApplicationDetailPage.vue'),
    props: { moduleNav: 'recruitment' },
    meta: { permission: 'recruitment_center.view' },
  },
  {
    path: 'recruitment/guest-apply',
    name: 'recruitment-guest-apply',
    component: () => import('@/features/recruitment/pages/RecruitmentGuestApplyPage.vue'),
    props: { moduleNav: 'recruitment' },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.guestApply',
    },
  },
  {
    path: 'recruitment/my-register-qr',
    name: 'recruitment-my-register-qr',
    redirect: { name: 'recruiter-workbench' },
  },
  {
    path: 'recruitment/portal-qr',
    name: 'recruitment-portal-qr',
    component: () => import('@/features/recruitment/pages/RecruitmentPortalQrPage.vue'),
    props: { moduleNav: 'recruitment' },
    meta: {
      permissions: ['lead.team_view', 'team.kpi.view', 'users.manage'],
      requiresBrowseAssignees: true,
      pageTitleKey: 'nav.items.recruitmentPortalQr',
    },
  },
  {
    path: 'recruitment/portal-referrers',
    name: 'recruitment-portal-referrers',
    component: () => import('@/features/recruitment/pages/PortalReferrersPage.vue'),
    props: { moduleNav: 'recruitment', scopeMine: true },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.portalReferrers',
    },
  },
  {
    path: 'applications-interviews/referrers',
    name: 'application-referrers',
    component: () => import('@/features/recruitment/pages/PortalReferrersPage.vue'),
    props: { moduleNav: 'applications', scopeMine: false },
    meta: {
      permission: 'applications.view',
      pageTitleKey: 'nav.items.applicationReferrers',
    },
  },
  {
    path: 'recruitment/active-employment',
    name: 'recruitment-active-employment',
    component: () => import('@/features/recruitment/pages/ActiveEmploymentPage.vue'),
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.activeEmployment',
      moduleNav: 'recruitment',
    },
  },
  {
    path: 'recruitment/resigned-recall',
    name: 'recruitment-resigned-recall',
    component: () => import('@/features/leads/pages/LeadLibraryPage.vue'),
    props: {
      workbenchSegment: 'employment_recall',
      lockWorkbenchTabs: true,
      moduleNav: 'recruitment',
      pageTitleKey: 'nav.items.resignedRecall',
    },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.resignedRecall',
    },
  },
  { path: 'recruitment/leads/personal', name: 'lead-library-personal', redirect: { name: 'recruiter-workbench' } },

  // 03 Resource center
  {
    path: 'resources/import',
    name: 'resource-import',
    component: () => import('@/features/leads/pages/CommunityResourceWorkspacePage.vue'),
    meta: {
      permission: 'resource_center.view',
      permissions: ['assignments.manage', 'lead_import.create', 'lead_import.review', 'resource_center.view'],
      pageTitleKey: 'nav.items.resourceImport',
      moduleNav: 'resource',
    },
  },
  {
    path: 'recruitment/leads/public',
    name: 'lead-library-public',
    component: () => import('@/features/leads/pages/CommunityLibraryPage.vue'),
    props: { moduleNav: 'resource' },
    meta: {
      permission: 'resource_center.view',
      permissions: ['assignments.manage', 'raw_lead.view', 'lead_import.batch_view', 'source_quality.review'],
      pageTitleKey: 'nav.items.rawLeads',
      moduleNav: 'resource',
    },
  },
  {
    path: 'system/settings/channels',
    name: 'settings-channels',
    component: () => import('@/features/leads/pages/TrafficEntriesPage.vue'),
    props: { moduleNav: 'resource' },
    meta: {
      permission: 'resource_center.view',
      permissions: ['source.view', 'source.create', 'source.edit', 'source.manage_all', 'channel.view', 'channel.manage'],
      pageTitleKey: 'nav.items.trafficEntries',
      moduleNav: 'resource',
    },
  },
  {
    path: 'resources/entry-forms',
    name: 'resource-entry-forms',
    component: () => import('@/features/leads/pages/TrafficEntryFormsPage.vue'),
    props: { moduleNav: 'resource' },
    meta: {
      permission: 'resource_center.view',
      permissions: ['qr_form.create', 'campaign.view', 'campaign.create', 'campaign.edit', 'campaign.manage_all', 'channel.view', 'channel.manage'],
      pageTitleKey: 'nav.items.entryForms',
      moduleNav: 'resource',
    },
  },
  zaloRoutingRoute,
  zaloRoutingLegacyRoute,
  {
    path: 'resources/distribution',
    name: 'resource-distribution',
    component: () => import('@/features/leads/pages/ResourceDistributionPage.vue'),
    meta: {
      permission: 'resource_center.view',
      permissions: ['assignments.manage', 'lead.transfer', 'resource_center.view'],
      pageTitleKey: 'nav.items.resourceDistribution',
      moduleNav: 'resource',
    },
  },
  {
    path: 'resources/assignment-rules',
    name: 'resource-assignment-rules',
    component: () => import('@/features/leads/pages/AssignmentRulesPage.vue'),
    meta: {
      permission: 'resource_center.view',
      permissions: ['rules.manage', 'resource_center.view'],
      pageTitleKey: 'nav.items.assignmentRules',
      moduleNav: 'resource',
    },
  },
  {
    path: 'system/settings/rules',
    name: 'rules',
    component: () => import('@/features/settings/pages/RulesPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: { permission: 'rules.manage', pageTitleKey: 'nav.items.settingsAssignmentRules' },
  },
  {
    path: 'resources/assignment-records',
    name: 'resource-assignment-records',
    component: () => import('@/features/leads/pages/ResourceDistributionHistoryPage.vue'),
    meta: {
      permission: 'resource_center.view',
      permissions: ['assignments.manage', 'lead.team_view', 'team.kpi.view'],
      pageTitleKey: 'nav.items.assignmentRecords',
      moduleNav: 'resource',
    },
  },
  {
    path: 'resources/reclaim-records',
    name: 'resource-reclaim-records',
    component: () => import('@/features/leads/pages/ReclaimHistoryPage.vue'),
    meta: {
      permission: 'resource_center.view',
      permissions: ['assignments.manage', 'lead.team_view', 'team.kpi.view'],
      pageTitleKey: 'nav.items.reclaimRecords',
      moduleNav: 'resource',
    },
  },
  {
    path: 'resources/public-pool-claims',
    name: 'resource-public-pool-claims',
    component: () => import('@/features/leads/pages/PublicPoolClaimHistoryPage.vue'),
    meta: {
      permission: 'resource_center.view',
      permissions: ['assignments.manage', 'lead.team_view', 'team.kpi.view'],
      pageTitleKey: 'nav.items.publicPoolClaims',
      moduleNav: 'resource',
    },
  },
  {
    path: 'resources/public-pool',
    name: 'resource-public-pool',
    component: () => import('@/features/leads/pages/ResumePoolPage.vue'),
    props: { moduleNav: 'resource', pageTitleKey: 'nav.items.publicPool' },
    meta: {
      permission: 'resource_center.view',
      pageTitleKey: 'nav.items.publicPool',
      moduleNav: 'resource',
    },
  },
  {
    path: 'recruitment/public-pool',
    name: 'recruitment-public-pool',
    component: () => import('@/features/leads/pages/ResumePoolPage.vue'),
    props: { moduleNav: 'recruitment', pageTitleKey: 'nav.items.publicPool' },
    meta: {
      permission: 'recruitment_center.view',
      pageTitleKey: 'nav.items.publicPool',
      moduleNav: 'recruitment',
    },
  },
  { path: 'recruitment/leads/resume-pool', name: 'lead-library-resume-pool', redirect: { name: 'resource-public-pool' } },
  {
    path: 'resources/recycled',
    name: 'resource-recycled',
    component: () => import('@/features/leads/pages/RecycledResourcesPage.vue'),
    props: { moduleNav: 'resource', pageTitleKey: 'nav.items.recycledResources' },
    meta: {
      permission: 'resource_center.view',
      permissions: [
        'assignments.manage',
        'lead.owner_edit_after_distribution',
        'resource_center.view',
      ],
      pageTitleKey: 'nav.items.recycledResources',
      moduleNav: 'resource',
    },
  },
  {
    path: 'system/settings/blacklist',
    name: 'blacklist',
    component: () => import('@/features/settings/pages/BlacklistPage.vue'),
    props: { moduleNav: 'resource' },
    meta: {
      permission: 'resource_center.view',
      permissions: [
        'integrations.manage',
        'data_exception.view',
        'data_exception.handle',
        'duplicate_lead.review',
        'duplicate_worker.review',
      ],
      pageTitleKey: 'nav.items.blacklistInvalid',
    },
  },

  // 04 Applications & interviews
  {
    path: 'recruitment/applications',
    name: 'applications',
    component: () => import('@/features/applications/pages/ApplicationListPage.vue'),
    props: { moduleNav: 'applications' },
    meta: { permission: 'applications.view', pageTitleKey: 'nav.items.applicationList' },
  },
  {
    path: 'recruitment/applications/:id',
    name: 'application-detail',
    component: () => import('@/features/applications/pages/ApplicationDetailPage.vue'),
    props: { moduleNav: 'applications' },
    meta: { permission: 'applications.view' },
  },
  {
    path: 'applications-interviews/create',
    name: 'application-create',
    component: () => import('@/features/applications/pages/ApplicationCreatePage.vue'),
    props: { moduleNav: 'applications' },
    meta: {
      permission: 'applications.create',
      pageTitleKey: 'nav.items.applicationCreate',
      moduleNav: 'applications',
    },
  },
  {
    path: 'applications-interviews/schedule',
    name: 'interview-schedule',
    component: () => import('@/features/applications/pages/ApplicationListPage.vue'),
    props: {
      moduleNav: 'applications',
      pageTitleKey: 'nav.items.interviewSchedule',
      pageSubtitleKey: 'applications.scheduleSubtitle',
      listView: 'schedule',
    },
    meta: { permission: 'applications.view', pageTitleKey: 'nav.items.interviewSchedule', moduleNav: 'applications' },
  },
  {
    path: 'applications-interviews/awaiting-result',
    name: 'interview-awaiting-result',
    component: () => import('@/features/applications/pages/ApplicationListPage.vue'),
    props: {
      moduleNav: 'applications',
      pageTitleKey: 'nav.items.interviewAwaitingResult',
      pageSubtitleKey: 'applications.awaitingResultSubtitle',
      listView: 'awaiting_result',
    },
    meta: { permission: 'applications.view', pageTitleKey: 'nav.items.interviewAwaitingResult', moduleNav: 'applications' },
  },
  {
    path: 'applications-interviews/results',
    name: 'interview-results',
    component: () => import('@/features/applications/pages/ApplicationListPage.vue'),
    props: {
      moduleNav: 'applications',
      pageTitleKey: 'nav.items.interviewResults',
      pageSubtitleKey: 'applications.resultsSubtitle',
      listView: 'results',
    },
    meta: { permission: 'applications.view', pageTitleKey: 'nav.items.interviewResults', moduleNav: 'applications' },
  },
  {
    path: 'applications-interviews/refused',
    name: 'interview-refused',
    component: () => import('@/features/applications/pages/ApplicationListPage.vue'),
    props: {
      moduleNav: 'applications',
      pageTitleKey: 'nav.items.interviewRefused',
      pageSubtitleKey: 'applications.refusedSubtitle',
      listView: 'refused',
    },
    meta: { permission: 'applications.view', pageTitleKey: 'nav.items.interviewRefused', moduleNav: 'applications' },
  },
  {
    path: 'applications-interviews/no-show',
    name: 'no-show-list',
    component: () => import('@/features/applications/pages/ApplicationListPage.vue'),
    props: {
      moduleNav: 'applications',
      pageTitleKey: 'nav.items.noShowList',
      pageSubtitleKey: 'applications.noShowSubtitle',
      listView: 'no_show',
    },
    meta: { permission: 'applications.view', pageTitleKey: 'nav.items.noShowList', moduleNav: 'applications' },
  },

  // 05 Worker archive
  {
    path: 'workers',
    name: 'workers',
    component: () => import('@/features/workers/pages/WorkerListPage.vue'),
    props: { moduleNav: 'workerArchive' },
    meta: { permission: 'workers.view', pageTitleKey: 'nav.items.workerProfiles' },
  },
  { path: 'workers/detail', name: 'worker-detail-hub', redirect: { name: 'workers' } },
  {
    path: 'workers/employment-history',
    name: 'worker-employment-history',
    component: () => import('@/features/onsite/pages/DeploymentListPage.vue'),
    props: {
      presetScreen: 'employment_history',
      presetView: 'employment_history',
      moduleNav: 'workerArchive',
      pageTitleKey: 'nav.items.employmentHistory',
      pageSubtitleKey: 'workers.employmentHistory.subtitle',
      panelTitleKey: 'workers.employmentHistory.panelTitle',
    },
    meta: { permission: 'workers.view', pageTitleKey: 'nav.items.employmentHistory' },
  },
  {
    path: 'workers/referral-relations',
    name: 'worker-referral-relations',
    component: () => import('@/features/workers/pages/WorkerReferralRelationsPage.vue'),
    props: { moduleNav: 'workerArchive' },
    meta: { permission: 'workers.view', pageTitleKey: 'nav.items.referralRelations' },
  },
  {
    path: 'workers/tags-relations',
    name: 'worker-tags-relations',
    component: () => import('@/features/workers/pages/WorkerTagsRelationsPage.vue'),
    props: { moduleNav: 'workerArchive' },
    meta: { permission: 'workers.view', pageTitleKey: 'nav.items.tagsAndMembers' },
  },
  {
    path: 'workers/blacklist',
    name: 'worker-blacklist',
    component: () => import('@/features/workers/pages/WorkerBlacklistPage.vue'),
    props: { moduleNav: 'workerArchive' },
    meta: { permission: 'workers.view', pageTitleKey: 'nav.items.workerBlacklist' },
  },
  {
    path: 'workers/:id',
    name: 'worker-detail',
    component: () => import('@/features/workers/pages/WorkerDetailPage.vue'),
    props: { moduleNav: 'workerArchive' },
    meta: { permission: 'workers.view' },
  },

  // 06 Onsite
  { path: 'onsite', name: 'onsite', redirect: (to) => ({ name: 'onsite-pickup-reminder', query: to.query }) },
  { path: 'onsite/tasks', name: 'onsite-tasks', redirect: (to) => ({ name: 'onsite-pickup-reminder', query: to.query }) },
  {
    path: 'onsite/pickup',
    name: 'onsite-pickup-reminder',
    component: () => import('@/features/onsite/pages/DeploymentListPage.vue'),
    props: {
      presetView: 'pickup',
      presetFocus: 'pickup_pending',
      moduleNav: 'onsite',
      pageTitleKey: 'nav.items.pickupReminder',
      lockFocusFilter: true,
    },
    meta: { permission: 'onsite.view', pageTitleKey: 'nav.items.pickupReminder' },
  },
  {
    path: 'onsite/training',
    name: 'onsite-training-list',
    component: () => import('@/features/onsite/pages/DeploymentListPage.vue'),
    props: {
      presetView: 'training_list',
      moduleNav: 'onsite',
      pageTitleKey: 'nav.items.trainingList',
      pageSubtitleKey: 'onsite.trainingList.subtitle',
      panelTitleKey: 'onsite.trainingList.panelTitle',
    },
    meta: { permission: 'onsite.view', pageTitleKey: 'nav.items.trainingList' },
  },
  {
    path: 'onsite/onboarding',
    name: 'onsite-onboarding-confirm',
    component: () => import('@/features/onsite/pages/DeploymentListPage.vue'),
    props: {
      presetView: 'onboarding_confirm',
      moduleNav: 'onsite',
      pageTitleKey: 'nav.items.onboardingConfirm',
      pageSubtitleKey: 'onsite.onboardingConfirm.subtitle',
      panelTitleKey: 'onsite.onboardingConfirm.panelTitle',
      lockStatusFilter: true,
      lockFocusFilter: true,
    },
    meta: { permission: 'onsite.view', pageTitleKey: 'nav.items.onboardingConfirm' },
  },
  {
    path: 'onsite/active-tracking',
    name: 'onsite-active-tracking',
    component: () => import('@/features/onsite/pages/DeploymentListPage.vue'),
    props: {
      presetView: 'active_tracking',
      moduleNav: 'onsite',
      pageTitleKey: 'nav.items.activeTracking',
      pageSubtitleKey: 'onsite.activeTracking.subtitle',
      panelTitleKey: 'onsite.activeTracking.panelTitle',
    },
    meta: { permission: 'onsite.view', pageTitleKey: 'nav.items.activeTracking' },
  },
  factoryImportPage(
    'factory-import/training',
    'factory-import-training',
    'training_list',
    'nav.items.factoryImportTraining',
    'onsite.factoryImport.trainingSubtitle',
  ),
  factoryImportPage(
    'factory-import/onboard',
    'factory-import-onboard',
    'onboard_list',
    'nav.items.factoryImportOnboard',
    'onsite.factoryImport.onboardSubtitle',
  ),
  factoryImportPage(
    'factory-import/active',
    'factory-import-active',
    'active_roster',
    'nav.items.factoryImportActive',
    'onsite.factoryImport.activeSubtitle',
  ),
  factoryImportPage(
    'factory-import/resigned',
    'factory-import-resigned',
    'resigned_roster',
    'nav.items.factoryImportResigned',
    'onsite.factoryImport.resignedSubtitle',
  ),
  factoryImportPage(
    'factory-import/attendance',
    'factory-import-attendance',
    'attendance_list',
    'nav.items.factoryImportAttendance',
    'onsite.factoryImport.attendanceSubtitle',
  ),
  {
    path: 'factory-import/interview-results',
    name: 'factory-import-interview-results',
    component: () => import('@/features/onsite/pages/InterviewResultImportPage.vue'),
    meta: {
      permissions: [
        'applications.operate',
        'onsite.view',
        'onsite.edit',
        'factory_list.import',
      ],
      pageTitleKey: 'nav.items.factoryImportInterviewResult',
      pageSubtitleKey: 'onsite.factoryImport.interviewResultSubtitle',
    },
  },
  {
    path: 'onsite/factory-import',
    name: 'onsite-factory-import',
    redirect: (to) => ({
      name: 'factory-import-onboard',
      query: to.query,
    }),
  },
  {
    path: 'onsite/employment-matching',
    name: 'onsite-employment-matching',
    redirect: (to) => ({
      name: 'factory-import-onboard',
      query: to.query,
    }),
  },
  {
    path: 'onsite/onsite-abort',
    name: 'onsite-abort',
    component: () => import('@/features/onsite/pages/DeploymentListPage.vue'),
    props: {
      presetView: 'onsite_abort',
      moduleNav: 'onsite',
      pageTitleKey: 'nav.items.onsiteAbort',
      pageSubtitleKey: 'onsite.onsiteAbort.subtitle',
      panelTitleKey: 'onsite.onsiteAbort.panelTitle',
    },
    meta: { permission: 'onsite.view', pageTitleKey: 'nav.items.onsiteAbort' },
  },
  {
    path: 'onsite/resignation-feedback',
    name: 'onsite-resignation-feedback',
    component: () => import('@/features/onsite/pages/ResignationFeedbackPage.vue'),
    props: {
      moduleNav: 'onsite',
      pageTitleKey: 'nav.items.resignationFeedback',
      pageSubtitleKey: 'onsite.resignationFeedback.subtitle',
    },
    meta: { permission: 'onsite.view', pageTitleKey: 'nav.items.resignationFeedback' },
  },
  // Legacy bookmark — retention 7/30/90 lives on active-tracking.
  { path: 'onsite/reward-milestones', redirect: { name: 'onsite-active-tracking' } },

  // 07 Enterprise & jobs — PDF §07
  { path: 'enterprise', redirect: { name: 'enterprise-clients' } },
  {
    path: 'enterprise/clients',
    name: 'enterprise-clients',
    component: () => import('@/features/enterprise/pages/EnterpriseClientsPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: { permission: 'enterprise.view', pageTitleKey: 'nav.items.enterpriseClients' },
  },
  {
    path: 'enterprise/clients/:id',
    name: 'enterprise-client-detail',
    component: () => import('@/features/enterprise/pages/EnterpriseClientDetailPage.vue'),
    props: (route) => ({ id: route.params.id, moduleNav: 'enterprise' }),
    meta: { permission: 'enterprise.view', pageTitleKey: 'enterpriseClients.detailTitle' },
  },
  {
    path: 'enterprise/partner-send',
    name: 'enterprise-partner-send',
    component: () => import('@/features/enterprise/pages/PartnerSendQueuePage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: ['enterprise.view', 'partner_send.send', 'supplier.submission.review'],
      pageTitleKey: 'nav.items.partnerSendQueue',
    },
  },
  {
    path: 'enterprise/partner-send/form',
    name: 'enterprise-partner-send-form',
    component: () => import('@/features/enterprise/pages/PartnerSendFormPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: ['enterprise.view', 'partner_send.send', 'supplier.submission.review'],
      pageTitleKey: 'partnerSend.formTitle',
      viewportFill: true,
    },
  },
  {
    path: 'enterprise/partner-qr',
    name: 'enterprise-partner-qr',
    component: () => import('@/features/enterprise/pages/PartnerQrConfigPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: [
        'menu.enterprise_partner_qr.view',
        'menu.enterprise_partner_qr.operate',
        'partner_send.configure',
      ],
      pageTitleKey: 'nav.items.partnerQrConfig',
      viewportFill: true,
    },
  },
  {
    path: 'enterprise/orders',
    name: 'enterprise-orders',
    component: () => import('@/features/jobs/pages/JobListPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: [
        'job_orders.view',
        'enterprise_reward.attach',
        'parent_reward.attach',
        'enterprise_settlement.attach',
      ],
      pageTitleKey: 'nav.items.jobsOrders',
    },
  },
  {
    path: 'enterprise/orders/internal',
    redirect: { name: 'enterprise-orders' },
  },
  {
    path: 'enterprise/orders/supplier',
    redirect: { name: 'enterprise-orders' },
  },
  {
    path: 'enterprise/jobs',
    redirect: { name: 'enterprise-orders' },
  },
  {
    path: 'enterprise/orders/new',
    name: 'enterprise-order-create',
    component: () => import('@/features/jobs/pages/JobFormPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: ['job_orders.create'],
      pageTitleKey: 'jobs.createOrderTitle',
      viewportFill: true,
      // Keep instance across create → edit so submit does not remount + refetch catalogs.
      pageKey: 'enterprise-order-form',
    },
  },
  {
    path: 'enterprise/orders/new/internal',
    redirect: (to) => ({ name: 'enterprise-order-create', query: { ...to.query, audience: 'internal' } }),
  },
  {
    path: 'enterprise/orders/new/supplier',
    redirect: (to) => ({ name: 'enterprise-order-create', query: { ...to.query, audience: 'supplier' } }),
  },
  {
    path: 'enterprise/orders/:id/edit',
    name: 'enterprise-order-edit',
    component: () => import('@/features/jobs/pages/JobFormPage.vue'),
    props: (route) => ({ id: route.params.id, moduleNav: 'enterprise' }),
    meta: {
      permissions: [
        'job_orders.create',
        'job_orders.edit',
        'enterprise_reward.attach',
        'parent_reward.attach',
        'enterprise_settlement.attach',
      ],
      pageTitleKey: 'jobs.editOrderTitle',
      viewportFill: true,
      pageKey: 'enterprise-order-form',
    },
  },
  {
    path: 'enterprise/jobs/new/internal',
    redirect: (to) => ({ name: 'enterprise-order-create', query: { ...to.query, audience: 'internal' } }),
  },
  {
    path: 'enterprise/jobs/new/supplier',
    redirect: (to) => ({ name: 'enterprise-order-create', query: { ...to.query, audience: 'supplier' } }),
  },
  {
    path: 'enterprise/jobs/new',
    redirect: { name: 'enterprise-order-create' },
  },
  {
    path: 'enterprise/jobs/:id/edit',
    redirect: (to) => ({ name: 'enterprise-order-edit', params: to.params }),
  },
  {
    path: 'enterprise/supplier-access',
    redirect: { name: 'channel-supplier-order-access' },
  },
  {
    path: 'enterprise/order-access',
    name: 'enterprise-job-order-access',
    redirect: { name: 'channel-supplier-order-access' },
  },
  {
    path: 'enterprise/policies/rewards',
    name: 'enterprise-reward-policies',
    component: () => import('@/features/enterprise/pages/EnterpriseRewardPoliciesPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: ['worker_bonus.view', 'bonus_policy.create'],
      pageTitleKey: 'nav.items.workerRewardPolicies',
    },
  },
  {
    path: 'enterprise/policies/rewards/new',
    name: 'enterprise-reward-policy-create',
    // Create is JO-first; keep named route for old links but bounce to JO/hub.
    redirect: (to) => {
      const jobOrderId = String(to.query.job_order_id ?? '').trim()
      if (jobOrderId) {
        return {
          name: 'enterprise-order-edit',
          params: { id: jobOrderId },
          query: { tab: 'policies' },
        }
      }
      return {
        name: 'enterprise-reward-policies',
        query: { create_hint: '1', ...(to.query.client_id ? { client_id: to.query.client_id } : {}) },
      }
    },
  },
  {
    path: 'enterprise/policies/rewards/:id/edit',
    name: 'enterprise-reward-policy-edit',
    component: () => import('@/features/enterprise/pages/EnterpriseRewardPolicyFormPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: [
        'bonus_policy.create',
        'job_orders.create',
        'job_orders.edit',
        'enterprise.edit',
      ],
      pageTitleKey: 'enterpriseRewardPolicies.pageEditTitle',
    },
  },
  {
    path: 'enterprise/policies/reward-statements',
    name: 'enterprise-reward-statements',
    component: () => import('@/features/enterprise/pages/EnterpriseWorkerBonusRecordsPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: ['worker_bonus.view', 'worker_bonus.approve', 'worker_bonus.statement_create'],
      pageTitleKey: 'nav.items.workerRewardStatements',
    },
  },
  {
    path: 'enterprise/policies/settlement',
    name: 'enterprise-settlement-policies',
    component: () => import('@/features/enterprise/pages/EnterpriseSettlementPoliciesPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: ['enterprise_settlement.view', 'enterprise_settlement_rule.create', 'enterprise.view'],
      pageTitleKey: 'nav.items.enterpriseSettlementRules',
    },
  },
  {
    path: 'enterprise/policies/settlement-statements',
    name: 'enterprise-settlement-statements',
    component: () => import('@/features/enterprise/pages/EnterpriseSettlementStatementsPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: [
        'enterprise_settlement.view',
        'enterprise_settlement.create',
        'enterprise_settlement.adjust',
        'payment.record',
      ],
      pageTitleKey: 'nav.items.enterpriseSettlementPayments',
    },
  },
  // Settlement Rule Routes - standalone pages
  {
    path: 'settlement-rules/new',
    name: 'settlement-rule-form',
    // Create is JO-first; keep named route for old links but bounce to JO/hub.
    redirect: (to) => {
      const jobOrderId = String(to.query.job_order_id ?? '').trim()
      if (jobOrderId) {
        return {
          name: 'enterprise-order-edit',
          params: { id: jobOrderId },
          query: { tab: 'policies' },
        }
      }
      return {
        name: 'enterprise-settlement-policies',
        query: { create_hint: '1', ...(to.query.client_id ? { client_id: to.query.client_id } : {}) },
      }
    },
  },
  {
    path: 'settlement-rules/:id/edit',
    name: 'settlement-rule-edit',
    component: () => import('@/features/enterprise/pages/SettlementRuleFormPage.vue'),
    props: { moduleNav: 'enterprise' },
    meta: {
      permissions: ['enterprise_settlement.view', 'enterprise_settlement_rule.create', 'enterprise.view'],
      pageTitleKey: 'enterpriseWorkbench.clientSettlementRules.editAction',
    },
  },
  // Job Order Settlement Rules - embedded in Jobs module
  {
    path: 'jobs/:jobId/settlement-rules/new',
    name: 'job-settlement-rule-form',
    redirect: (to) => ({
      name: 'enterprise-order-edit',
      params: { id: String(to.params.jobId) },
      query: { tab: 'policies' },
    }),
  },
  {
    path: 'jobs/:jobId/settlement-rules/:id/edit',
    name: 'job-settlement-rule-edit',
    component: () => import('@/features/enterprise/pages/SettlementRuleFormPage.vue'),
    props: { moduleNav: 'jobs' },
    meta: {
      permissions: ['enterprise_settlement.view', 'enterprise_settlement_rule.create', 'enterprise.view'],
      pageTitleKey: 'enterpriseWorkbench.clientSettlementRules.editAction',
    },
  },
  {
    path: 'enterprise/policies/supplier-settlement',
    redirect: { name: 'channel-supplier-settlement-rules' },
  },
  // Legacy route names (pre unified enterprise flow)
  { path: 'enterprise/__legacy/jobs', name: 'jobs', redirect: { name: 'enterprise-orders' } },
  { path: 'enterprise/__legacy/jobs/new', name: 'job-create', redirect: { name: 'enterprise-order-create' } },
  {
    path: 'enterprise/__legacy/jobs/:id/edit',
    name: 'job-edit',
    redirect: (to) => ({ name: 'enterprise-order-edit', params: to.params }),
  },
  {
    path: 'system/settings/point-reward-rules',
    name: 'system-reward-rules',
    component: () => import('@/features/settings/pages/RewardRulesPage.vue'),
    meta: { permission: 'bonus_policy.create', pageTitleKey: 'settings.tabs.rewardRules' },
  },
  {
    path: 'system/settings/reward-rules',
    redirect: { name: 'system-reward-rules' },
  },
  {
    path: 'system/settings/settlement-rules',
    name: 'settlement-rules',
    component: () => import('@/features/settings/pages/SettlementRulesPage.vue'),
    meta: { permission: 'rules.manage', pageTitleKey: 'settings.tabs.settlementRules' },
  },
  { path: 'enterprise/channels', name: 'channel-portal', redirect: { name: 'resource-entry-forms' } },

  // 08 Channel suppliers — internal GEE management
  {
    path: 'channel-suppliers/list',
    name: 'channel-supplier-list',
    component: () => import('@/features/channel-suppliers/pages/SupplierListPage.vue'),
    props: { moduleNav: 'channelSuppliers' },
    meta: { permission: 'channel.view', pageTitleKey: 'nav.items.supplierList' },
  },
  {
    path: 'channel-suppliers/accounts',
    name: 'channel-supplier-accounts',
    component: () => import('@/features/channel-suppliers/pages/SupplierAccountsPage.vue'),
    props: { moduleNav: 'channelSuppliers' },
    meta: {
      permissions: ['channel.view', 'channel.manage', 'supplier_account.create'],
      pageTitleKey: 'nav.items.supplierAccounts',
    },
  },
  {
    path: 'channel-suppliers/order-access',
    name: 'channel-supplier-order-access',
    component: () => import('@/features/channel-suppliers/pages/SupplierOrderAccessPage.vue'),
    props: { moduleNav: 'channelSuppliers' },
    meta: {
      permissions: ['supplier_job_access.view', 'supplier_job_access.manage', 'supplier_settlement.attach'],
      pageTitleKey: 'nav.items.supplierOrderAccess',
    },
  },
  {
    path: 'channel-suppliers/submissions',
    name: 'channel-supplier-submissions',
    component: () => import('@/features/channel-suppliers/pages/SupplierSubmissionsPage.vue'),
    props: { moduleNav: 'channelSuppliers' },
    meta: {
      permissions: [
        'channel.view',
        'supplier.intake.view',
        'menu.supplier_submissions.view',
        'supplier.submission.review',
      ],
      pageTitleKey: 'nav.items.supplierSubmissions',
    },
  },
  {
    path: 'channel-suppliers/manual-exception-intake',
    name: 'channel-supplier-manual-exception-intake',
    component: () => import('@/features/channel-suppliers/pages/SupplierManualExceptionIntakePage.vue'),
    props: { moduleNav: 'applications' },
    meta: {
      permissions: ['supplier.submission.review'],
      pageTitleKey: 'suppliers.manualException.title',
      moduleNav: 'applications',
    },
  },
  {
    path: 'channel-suppliers/delivery-records',
    name: 'channel-delivery-records',
    component: () => import('@/features/channel-suppliers/pages/ChannelDeliveryRecordsPage.vue'),
    props: { moduleNav: 'channelSuppliers' },
    meta: { permission: 'channel.view', pageTitleKey: 'nav.items.channelDeliveryRecords' },
  },
  {
    path: 'channel-suppliers/contribution',
    name: 'channel-contribution',
    component: () => import('@/features/channel-suppliers/pages/ChannelContributionPage.vue'),
    props: { moduleNav: 'channelSuppliers' },
    meta: { permissions: ['channel.view', 'channel.manage'], pageTitleKey: 'nav.items.channelContribution' },
  },
  {
    path: 'channel-suppliers/settlement-rules',
    name: 'channel-supplier-settlement-rules',
    component: () => import('@/features/enterprise/pages/SupplierSettlementRulesPage.vue'),
    props: { moduleNav: 'channelSuppliers' },
    meta: {
      permissions: ['supplier_settlement.view', 'supplier_settlement.create', 'channel.view'],
      pageTitleKey: 'nav.items.channelSupplierSettlementRules',
    },
  },
  {
    path: 'channel-suppliers/settlement-statements',
    name: 'channel-supplier-settlement-statements',
    component: () => import('@/features/enterprise/pages/SupplierSettlementStatementsPage.vue'),
    props: { moduleNav: 'channelSuppliers' },
    meta: {
      permissions: [
        'supplier_settlement.view',
        'supplier_settlement.create',
        'supplier_settlement.lock',
        'payment.record',
      ],
      pageTitleKey: 'nav.items.channelSupplierSettlementStatements',
    },
  },
  {
    path: 'channel-suppliers/payments',
    name: 'channel-supplier-payments',
    redirect: {
      name: 'channel-supplier-settlement-statements',
      query: { tab: 'payables' },
    },
  },
  {
    path: 'channel-suppliers/settlement',
    name: 'channel-settlement',
    redirect: (to) => ({
      name: String(to.query.tab ?? '') === 'statements' ? 'channel-supplier-settlement-statements' : 'channel-supplier-settlement-rules',
      query: { ...to.query, tab: undefined },
    }),
  },

  // 09 Members
  {
    path: 'members/list',
    name: 'members-list',
    component: () => import('@/features/growth/pages/GrowthPage.vue'),
    props: { moduleNav: 'members', pageTitleKey: 'nav.items.memberList', fixedTab: 'membership', lockTabs: true },
    meta: { permissions: ['growth.view', 'growth.edit'], pageTitleKey: 'nav.items.memberList' },
  },
  { path: 'growth', name: 'growth', redirect: { name: 'members-list' } },
  {
    path: 'members/points',
    name: 'members-points',
    component: () => import('@/features/growth/pages/MemberPointsPage.vue'),
    props: { moduleNav: 'members' },
    meta: { permissions: ['growth.view', 'growth.edit'], pageTitleKey: 'nav.items.memberPoints' },
  },
  {
    path: 'members/referral-rewards',
    name: 'members-referral-rewards',
    component: () => import('@/features/growth/pages/MemberReferralRewardsPage.vue'),
    props: { moduleNav: 'members' },
    meta: { permissions: ['growth.view', 'growth.edit'], pageTitleKey: 'nav.items.memberReferralRewards' },
  },
  {
    path: 'members/miniapp-users',
    name: 'members-miniapp-users',
    component: () => import('@/features/growth/pages/MemberMiniappUsersPage.vue'),
    props: { moduleNav: 'members' },
    meta: { permissions: ['growth.view', 'growth.edit'], pageTitleKey: 'nav.items.memberMiniappUsers' },
  },

  // 11 Organization
  {
    path: 'system/departments',
    name: 'system-departments',
    component: () => import('@/features/system/pages/SystemDepartmentsPage.vue'),
    props: { moduleNav: 'organization' },
    meta: { permissions: ['users.manage', 'organization.view', 'organization.manage'] },
  },
  {
    path: 'system/teams',
    name: 'system-teams',
    component: () => import('@/features/system/pages/SystemTeamsPage.vue'),
    props: { moduleNav: 'organization' },
    meta: { permissions: ['users.manage', 'organization.view', 'organization.manage'] },
  },
  {
    path: 'system/users',
    name: 'system-users',
    component: () => import('@/features/system/pages/SystemUsersPage.vue'),
    props: { moduleNav: 'organization' },
    meta: { permissions: ['users.manage', 'organization.view', 'employees.view'] },
  },
  {
    path: 'system/roles',
    name: 'system-roles',
    component: () => import('@/features/system/pages/SystemRolesPage.vue'),
    props: { moduleNav: 'organization' },
    meta: { permissions: ['users.manage', 'roles.manage', 'permissions.manage'] },
  },
  { path: 'hr', redirect: { name: 'system-users' } },
  { path: 'hr/users', redirect: { name: 'system-users' } },
  { path: 'hr/teams', redirect: { name: 'system-teams' } },

  // 12 System settings
  {
    path: 'system/settings',
    name: 'system-settings',
    redirect: () => {
      const auth = useAuthStore()
      if (auth.can('settings.view')) return { name: 'settings-follow-up' }
      if (auth.can('rules.manage')) return { name: 'rules' }
      return { name: 'settings-system-profile' }
    },
  },
  {
    path: 'system/settings/follow-up',
    name: 'settings-follow-up',
    component: () => import('@/features/settings/pages/FollowUpCatalogPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: { permission: 'settings.view' },
  },
  {
    path: 'system/settings/status',
    name: 'settings-status-config',
    component: () => import('@/features/settings/pages/StatusConfigPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: { permission: 'settings.view', pageTitleKey: 'nav.items.settingsStatus' },
  },
  {
    path: 'system/settings/user-tags',
    name: 'settings-user-tags',
    component: () => import('@/features/settings/pages/TagsSettingsPage.vue'),
    props: { moduleNav: 'systemConfig', tagCategory: 'user' },
    meta: { permission: 'settings.view', pageTitleKey: 'nav.items.settingsTagConfig' },
  },
  {
    path: 'system/settings/enterprise-tags',
    name: 'settings-enterprise-tags',
    component: () => import('@/features/settings/pages/TagsSettingsPage.vue'),
    props: { moduleNav: 'systemConfig', tagCategory: 'client' },
    meta: { permission: 'settings.view', pageTitleKey: 'nav.items.settingsTagConfig' },
  },
  {
    path: 'system/settings/assignment-rules',
    name: 'settings-assignment-rules',
    redirect: { name: 'rules' },
  },
  {
    path: 'system/settings/channel-masters',
    name: 'system-channel-masters',
    component: () => import('@/features/settings/pages/ChannelSettingsPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: {
      permissions: ['channel.view', 'channel.manage'],
      pageTitleKey: 'nav.items.channelMasters',
      moduleNav: 'systemConfig',
    },
  },
  {
    path: 'system/settings/integrations',
    name: 'settings-integrations',
    component: () => import('@/features/settings/pages/PlatformIntegrationsPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: { permission: 'integrations.manage', pageTitleKey: 'nav.items.settingsApi' },
  },
  {
    path: 'system/settings/lead-platforms',
    name: 'settings-lead-platforms',
    component: () => import('@/features/settings/pages/LeadPlatformConnectionsPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: {
      permission: 'integrations.manage',
      pageTitleKey: 'nav.items.settingsLeadPlatforms',
    },
  },
  {
    path: 'system/settings/zalo-notifications',
    name: 'settings-zalo-notifications',
    component: () => import('@/features/settings/pages/ZaloNotificationTemplatesPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: {
      permission: 'integrations.manage',
      pageTitleKey: 'nav.items.settingsZaloNotifications',
    },
  },
  {
    path: 'system/settings/auto-notifications',
    name: 'settings-auto-notifications',
    component: () => import('@/features/settings/pages/AutoNotificationsPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: {
      permission: 'integrations.manage',
      pageTitleKey: 'nav.items.settingsAutoNotifications',
      pageSubtitleKey: 'settings.autoNotifications.subtitle',
    },
  },
  {
    path: 'system/settings/zalo-apis',
    name: 'settings-zalo-apis',
    redirect: { name: 'settings-zalo-notifications' },
  },
  {
    path: 'system/settings/internal-groups',
    name: 'settings-internal-groups',
    component: () => import('@/features/settings/pages/InternalNotificationGroupsPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: {
      permission: 'integrations.manage',
      pageTitleKey: 'nav.items.settingsInternalGroups',
      pageSubtitleKey: 'internalNotificationGroups.subtitle',
    },
  },
  {
    path: 'system/settings/audit-log',
    name: 'settings-audit-log',
    component: () => import('@/features/settings/pages/AuditLogPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: { permissions: ['audit_log.view', 'audit_log.view_limited', 'settings.edit'], pageTitleKey: 'nav.items.settingsAuditLog' },
  },
  {
    path: 'system/settings/menu-visibility',
    name: 'settings-menu-visibility',
    component: () => import('@/features/settings/pages/MenuVisibilityPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: { permission: 'settings.edit', pageTitleKey: 'settings.menuVisibility.title' },
  },
  {
    path: 'system/settings/system-profile',
    name: 'settings-system-profile',
    component: () => import('@/features/settings/pages/SystemProfilePage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: { permission: 'settings.view' },
  },
  {
    path: 'system/settings/tenants',
    name: 'settings-tenants',
    component: () => import('@/features/tenants/pages/TenantManagementPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: {
      requiresPlatformOrWorkspaceAdmin: true,
      pageTitleKey: 'tenants.manage.title',
    },
  },
  { path: 'system/settings/platform-info', redirect: { name: 'settings-system-profile' } },
  {
    path: 'system/settings/finance',
    name: 'finance-records',
    component: () => import('@/features/finance/pages/FinanceRecordsPage.vue'),
    props: { moduleNav: 'systemConfig' },
    meta: {
      permissions: [
        'payment_history.view',
        'enterprise_receivable.view',
        'supplier_payable.view',
      ],
    },
  },

  // Legacy redirects
  { path: 'dashboard', redirect: { name: 'home' } },
  { path: 'inbox', redirect: { name: 'recruiter-workbench' } },
  { path: 'applications', redirect: { name: 'applications' } },
  { path: 'jobs', redirect: { name: 'enterprise-orders' } },
  { path: 'jobs/new', redirect: { name: 'enterprise-order-create' } },
  { path: 'jobs/:id/edit', redirect: (to) => ({ name: 'enterprise-order-edit', params: to.params }) },
  { path: 'data-center/reports', redirect: { name: 'recruitment-reports' } },
  { path: 'recruitment/team-lead', name: 'team-lead', redirect: { name: 'recruitment-reports' } },
  { path: 'system', redirect: { name: 'system-users' } },
  { path: 'settings', name: 'settings-hub', redirect: { name: 'system-settings' } },
  { path: 'resources/rules', redirect: { name: 'resource-assignment-rules' } },
  { path: 'settings/rules', redirect: { name: 'rules' } },
  { path: 'settings/integrations', redirect: { name: 'settings-integrations' } },
  { path: 'settings/blacklist', redirect: { name: 'blacklist' } },
  { path: 'settings/masters', redirect: { name: 'system-settings' } },
  { path: 'settings/platforms', redirect: { name: 'settings-system-profile' } },
  { path: 'settings/:pathMatch(.*)*', redirect: { name: 'system-settings' } },
  { path: 'system/settings/domain-events', redirect: { name: 'system-settings' } },
  { path: 'system/settings/masters', redirect: { name: 'system-settings' } },
  { path: 'system/settings/platforms', redirect: { name: 'settings-system-profile' } },
]
